/**
 * DossierTOC — sticky table-of-contents jumper for /topic/<slug>.
 *
 * Renders nothing for the reader during normal scroll EXCEPT:
 *   • Desktop (≥1024px): a thin right-edge rail of 8 mono numerals — one per
 *     canonical section — that highlights the currently-visible one. Clicking
 *     a numeral smooth-scrolls to that section. The rail is `position: fixed`
 *     so it never breaks the reading flow.
 *   • Mobile (<1024px): a small floating "JUMP" pill in the bottom-right that
 *     opens a bottom-sheet listing the 8 sections. Tap to navigate. Sheet
 *     closes itself on selection.
 *
 * Design contract:
 *   1. ALWAYS shows 8 entries — matches the canonical hierarchy. If a section
 *      isn't in the DOM (rare), its entry stays disabled.
 *   2. Uses ONLY the existing `data-testid` attributes — no extra
 *      instrumentation needed on the sections themselves.
 *   3. IntersectionObserver drives the highlight, NOT scroll listeners. Quiet
 *      during normal reading, no jank on Android Chrome.
 *   4. Header offset is computed once from the existing sticky topbar height
 *      so smooth-scroll lands the heading just below the topbar, not under it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, X, ChevronRight } from "lucide-react";
import { useScrollIdle } from "../../hooks/useScrollIdle";
import { usePastHeader } from "../../hooks/usePastHeader";

const SECTIONS = [
  { id: "topic-detail-page",              label: "Hero" },
  { id: "dossier-exec-summary-section",   label: "Executive Summary" },
  { id: "leaked-excerpts",                label: "Leaked Excerpts" },
  { id: "hidden-truth-section",           label: "The Investigation" },
  { id: "dossier-timeline-section",       label: "Case Timeline" },
  { id: "dossier-key-players-section",    label: "Key People" },
  { id: "dossier-evidence-library-section", label: "Evidence Library" },
  { id: "dossier-sources-section",        label: "Sources" },
  { id: "dossier-connections-panel",      label: "Continue Investigating" },
];

const HEADER_OFFSET = 88; // sticky topbar height + breathing room

// One-shot scroll helper. Targets the section by its data-testid, accounts
// for the sticky header, and uses native smooth-scroll where supported.
const scrollToSection = (testid) => {
  if (typeof window === "undefined") return;
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
};

export const DossierTOC = () => {
  const [active, setActive] = useState(SECTIONS[0].id);
  const [presentIds, setPresentIds] = useState(() => new Set());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 — desktop-only fill
  const observerRef = useRef(null);
  const scrolling = useScrollIdle();
  const pastHeader = usePastHeader();

  // Scroll progress (desktop only). Uses rAF + a single passive listener so
  // it's quiet during normal reading. Respects `prefers-reduced-motion` —
  // when the user has motion reduced, we *snap* to the nearest 10% step
  // instead of animating smoothly, so the bar still informs but never
  // distracts.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const prefersReducedMotion = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
    let rafId = 0;
    const compute = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY;
      const max = (doc.scrollHeight || 0) - (window.innerHeight || 0);
      const raw = max > 0 ? Math.min(1, Math.max(0, scrolled / max)) : 0;
      const next = prefersReducedMotion ? Math.round(raw * 10) / 10 : raw;
      setProgress(next);
      rafId = 0;
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  // After mount, locate the sections actually in the DOM and observe them.
  // IntersectionObserver fires whenever a section enters/leaves the upper
  // 40% of the viewport — the "reading window" — which we treat as "active".
  useEffect(() => {
    const found = new Set();
    const targets = [];
    for (const s of SECTIONS) {
      const el = document.querySelector(`[data-testid="${s.id}"]`);
      if (el) { found.add(s.id); targets.push(el); }
    }
    setPresentIds(found);
    if (!targets.length) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest intersection ratio that's currently
        // intersecting; fall back to the first intersecting one.
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;
        visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const tid = visible[0].target.getAttribute("data-testid");
        if (tid) setActive(tid);
      },
      {
        // The "active band" is the top ~40% of the viewport — feels natural
        // because the heading is usually near the top of the screen when
        // you're reading the body underneath.
        rootMargin: `-${HEADER_OFFSET}px 0px -55% 0px`,
        threshold: [0, 0.1, 0.3, 0.6],
      },
    );
    targets.forEach((t) => obs.observe(t));
    observerRef.current = obs;
    return () => obs.disconnect();
  }, []);

  const onJump = useCallback((id) => {
    if (!presentIds.has(id)) return;
    scrollToSection(id);
    setActive(id);
    setMobileOpen(false);
  }, [presentIds]);

  const entries = useMemo(() => SECTIONS.map((s, i) => ({
    ...s,
    n: i + 1,
    present: presentIds.has(s.id),
    isActive: active === s.id,
  })), [active, presentIds]);

  return (
    <>
      {/* ── DESKTOP RAIL ─────────────────────────────────────────────── */}
      <nav
        aria-label="Dossier sections"
        className="hidden lg:flex fixed right-6 xl:right-10 top-1/2 -translate-y-1/2 z-30 flex-col gap-1 select-none"
        data-testid="dossier-toc-rail"
      >
        {/* Subtle vertical scroll-progress fill — sits flush at the left edge
            of the rail, alongside the numerals. Absolutely positioned so it
            never shifts the rail's layout. Pointer-events off so it can't
            interfere with the jumper buttons. Desktop only. */}
        <span
          aria-hidden="true"
          data-testid="dossier-toc-progress-fill"
          data-progress={progress.toFixed(3)}
          className="hidden lg:block pointer-events-none absolute -left-3 top-0 w-px bg-[#D92D20]/55 origin-top transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={{ height: "100%", transform: `scaleY(${progress})` }}
        />
        {/* Faint full-height track behind the fill — orientation marker only */}
        <span
          aria-hidden="true"
          className="hidden lg:block pointer-events-none absolute -left-3 top-0 w-px h-full bg-[#3a3530]/60"
        />
        {entries.map(({ id, n, label, present, isActive }) => (
          <button
            key={id}
            type="button"
            onClick={() => onJump(id)}
            disabled={!present}
            title={label}
            aria-label={`Jump to ${label}`}
            aria-current={isActive ? "true" : undefined}
            data-testid={`toc-rail-${id}`}
            className={
              "group flex items-center gap-2 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] " +
              "transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed " +
              (isActive
                ? "text-[#D92D20]"
                : "text-[#757065] hover:text-[#A39E93]")
            }
          >
            <span className={"inline-block w-6 text-right tabular-nums " + (isActive ? "font-semibold" : "")}>
              0{n}
            </span>
            <span
              aria-hidden
              className={
                "inline-block h-[1px] transition-all duration-300 " +
                (isActive ? "w-10 bg-[#D92D20]" : "w-4 bg-[#3a3530] group-hover:w-6 group-hover:bg-[#A39E93]")
              }
            />
            <span className={
              "hidden xl:inline-block transition-opacity duration-200 " +
              (isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100")
            }>
              {label}
            </span>
          </button>
        ))}
      </nav>

      {/* ── MOBILE FLOATING PILL ─────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="Jump to section"
        data-testid="dossier-toc-mobile-trigger"
        className={
          "lg:hidden fixed right-4 z-30 flex items-center gap-1.5 px-3 py-2 border border-[#D92D20] " +
          "bg-[#0a0907]/95 backdrop-blur-sm text-[#D92D20] font-mono text-[10px] uppercase tracking-[0.25em] shadow-lg " +
          "transition-opacity duration-200 " +
          ((scrolling || !pastHeader) && !mobileOpen ? "opacity-0 pointer-events-none" : "opacity-100")
        }
        style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <List size={12} /> Jump
      </button>

      {/* ── MOBILE BOTTOM SHEET ──────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 flex items-end"
          data-testid="dossier-toc-mobile-sheet"
        >
          {/* Overlay */}
          <button
            type="button"
            aria-label="Close section jumper"
            data-testid="dossier-toc-mobile-overlay"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <div className="relative w-full bg-[#0a0907] border-t-2 border-[#D92D20] p-4 pb-7">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
                // Jump to Section
              </span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close"
                className="text-[#A39E93] hover:text-[#D92D20]"
              >
                <X size={16} />
              </button>
            </div>
            <ul className="space-y-0.5">
              {entries.map(({ id, n, label, present, isActive }) => (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => onJump(id)}
                    disabled={!present}
                    aria-current={isActive ? "true" : undefined}
                    data-testid={`toc-sheet-${id}`}
                    className={
                      "w-full flex items-center justify-between gap-3 px-3 py-3 border " +
                      "font-mono text-[11px] uppercase tracking-[0.2em] transition-colors " +
                      "disabled:opacity-30 disabled:cursor-not-allowed " +
                      (isActive
                        ? "border-[#D92D20] bg-[#1a0808] text-[#D92D20]"
                        : "border-[#2D2A26] text-[#A39E93] hover:border-[#A39E93] hover:text-[#E4DFD3]")
                    }
                  >
                    <span className="flex items-center gap-3">
                      <span className="tabular-nums opacity-70">0{n}</span>
                      <span>{label}</span>
                    </span>
                    <ChevronRight size={12} className="opacity-70" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default DossierTOC;
